import React, { useState } from 'react';
import { useUserRole } from '../hooks/useUserRole';
import autoPartsService from '../services/autoPartsService';
import { dvlaLookup } from '../services/firebaseFunctions';

/**
 * Admin test page for DVLA + local car catalog (auto-parts-db / car-api).
 */
const TestAdmin = () => {
  const { role } = useUserRole();
  const [reg, setReg] = useState('');
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [parts, setParts] = useState([]);
  const [catalogMeta, setCatalogMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLookup = async (e) => {
    e.preventDefault();
    const r = reg.trim().toUpperCase().replace(/\s+/g, '');
    if (!r || r.length < 2) {
      setError('Enter a valid registration number');
      return;
    }
    setError(null);
    setVehicleInfo(null);
    setParts([]);
    setCatalogMeta(null);
    setLoading(true);

    try {
      const dvla = await dvlaLookup(r);
      const make = (dvla.make || '').trim();
      const model = (dvla.model || '').trim();
      const year = dvla.yearOfManufacture ? String(dvla.yearOfManufacture) : '';

      setVehicleInfo({ make, model, year, reg: r });

      const catalogParts = await autoPartsService.getPartsForVehicleFromCatalog(
        { make, model, year },
        5,
        20
      );
      setParts(catalogParts);

      const brands = await autoPartsService.getCarBrands();
      const brandModels = make
        ? await autoPartsService.getCarModelsByBrand(make)
        : [];
      setCatalogMeta({
        brandCount: brands.length,
        modelCount: brandModels.length,
        sampleBrands: brands.slice(0, 12),
      });
    } catch (err) {
      setError(err.message || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vehicle-inner">
      <div className="vehicle-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Admin Test Page</h2>
      </div>
      <p className="vehicle-subtitle" style={{ margin: 0, width: '100%' }}>
        This page is only visible to admin and owner roles.
      </p>
      <div style={{ marginTop: '24px', padding: '16px', background: 'var(--sidebar-icon-bg, rgba(0,0,0,0.05))', borderRadius: '8px' }}>
        <p style={{ margin: 0, fontWeight: 500 }}>Current role: {role || '—'}</p>
      </div>

      <section style={{ marginTop: '32px' }}>
        <h3 style={{ margin: '0 0 12px 0' }}>Reg → Car → Parts Lookup</h3>
        <p style={{ margin: '0 0 16px 0', color: 'var(--text-muted, #666)' }}>
          Enter a UK registration. DVLA resolves the car; the local car catalog (auto-parts-db) validates make/model and returns catalog parts.
        </p>
        <form onSubmit={handleLookup} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '14px' }}>Registration</span>
            <input
              type="text"
              value={reg}
              onChange={(e) => setReg(e.target.value)}
              placeholder="e.g. AB12 CDE"
              className="form-input"
              style={{ padding: '8px 12px', minWidth: '140px' }}
            />
          </label>
          <button type="submit" className="hero-button hero-primary" disabled={loading}>
            {loading ? 'Looking up…' : 'Look up parts'}
          </button>
        </form>
        {error && (
          <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(239,68,68,0.1)', borderRadius: '8px' }}>
            <p style={{ margin: 0, color: '#dc2626' }}>{error}</p>
          </div>
        )}
        {vehicleInfo && (
          <div style={{ marginTop: '16px', padding: '16px', background: 'var(--sidebar-icon-bg, rgba(0,0,0,0.05))', borderRadius: '8px' }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>Vehicle</p>
            <p style={{ margin: 0 }}>
              {vehicleInfo.reg} — {vehicleInfo.make} {vehicleInfo.model}
              {vehicleInfo.year ? ` (${vehicleInfo.year})` : ''}
            </p>
          </div>
        )}
        {catalogMeta && (
          <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-muted, #666)' }}>
            Catalog: {catalogMeta.brandCount} brands
            {catalogMeta.modelCount ? ` · ${catalogMeta.modelCount} models for this make` : ''}
            {catalogMeta.sampleBrands?.length ? (
              <div style={{ marginTop: '6px' }}>
                Sample brands: {catalogMeta.sampleBrands.join(', ')}
              </div>
            ) : null}
          </div>
        )}
        {parts.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>Parts ({parts.length})</p>
            <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid var(--border-color, rgba(0,0,0,0.1))', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: 'var(--sidebar-icon-bg, rgba(0,0,0,0.05))' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Part No</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Product</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Category</th>
                  </tr>
                </thead>
                <tbody>
                  {parts.map((p, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border-color, rgba(0,0,0,0.08))' }}>
                      <td style={{ padding: '8px 12px' }}>{p.articleNo || '—'}</td>
                      <td style={{ padding: '8px 12px' }}>{p.productName || '—'}</td>
                      <td style={{ padding: '8px 12px' }}>{p.categoryName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default TestAdmin;
